import { Nested, OnQuery, OnSignal } from 'dreamstate';
import { v4 as uuid } from 'uuid';

import { Maybe, Optional } from '@vimeo/core/types/generic';
import { Logger } from '@vimeo/logging';

import { queryManagerGctlConfig } from '../../../api/base';
import { getStreamHealthCredentials, getBroadcasterComposerConfig } from '../../../api/firebase';
import { postInteractionRoomAuth } from '../../../api/interaction';
import { IVimeoApiConnection } from '../../../types/api';
import {
  IFirebaseComposerCredentials,
  ILiveComposerApplicationConfig,
  IStreamHealthCredentials,
} from '../../../types/broadcaster';
import { IBroadcasterComposerSessionInfoMetadata } from '../../../types/composer_session';
import { IFirebaseDependencies, TFirebaseApp, TFirebaseUser } from '../../../types/firebase';
import { IInteractionApplicationUser } from '../../../types/interaction_room';
import {
  IFirebaseInteractionCredentials,
  IGuestInteractionApplicationConfig,
} from '../../../types/interaction_tools';
import { UnsubscribingContextManager } from '../../../utils/store/unsubscribing_manager';
import { ELiveConnectionQuery, ELiveInteractionQuery } from '../../queries';
import {
  EDependencySignal,
  ELiveSignal,
  TComposerSessionReadySignalEvent,
  TFirebaseDependenciesLoadedSignalEvent,
} from '../../signals';
import { dispose } from './AbstractFirebaseManager.actions';
import { tryToSetupComposerConnection } from './AbstractFirebaseManager.composerActions';
import {
  disposeInteractionToolsConnection,
  tryToSetupInteractionConnection,
} from './AbstractFirebaseManager.interactionActions';

export interface IFirebaseContext {
  /**
   * Interaction connection management.
   */
  isInteractionReady: boolean;
  interactionCredentials: Optional<IFirebaseInteractionCredentials>;
  streamHealthCredentials: Optional<IStreamHealthCredentials>;
  isStreamHealthReady: boolean;
  streamHealthConnection: Nested<{
    isConnected: boolean;
    user: Optional<TFirebaseUser>;
  }>;
  interactionConnection: Nested<{
    isConnected: boolean;
    user: Optional<TFirebaseUser>;
  }>;
  /**
   * Composer connection management.
   */
  isComposerReady: boolean;
  composerCredentials: Optional<IFirebaseComposerCredentials>;
  composerConnection: Nested<{
    isConnected: boolean;
    user: Optional<TFirebaseUser>;
  }>;
  /**
   * Generic fields.
   */
  isFirebaseReady: boolean;
  isDisposed: boolean;
  connectionStatuses: {
    isInteractionConnected: boolean;
  };
}

export abstract class AbstractFirebaseManager<
  T,
> extends UnsubscribingContextManager<IFirebaseContext> {
  public abstract override context: IFirebaseContext & T;

  /**
   * Dependencies for lazy loaded usage.
   */
  public firebase: Optional<IFirebaseDependencies> = null;

  /**
   * UUID of current manager instance to prevent sharing of applications between connections.
   */
  public uuid: string = uuid();

  /**
   * Internal store of initialized applications.
   */
  public interactionApp: Optional<TFirebaseApp> = null;
  public composerApp: Optional<TFirebaseApp> = null;
  public streamHealthApp: Optional<TFirebaseApp> = null;

  public readonly log: Logger = new Logger('🧁FBS');

  /**
   * Disconnect from firebase and dispose all listeners in case of generic unload.
   */
  public override onProvisionEnded(): void {
    return dispose(this);
  }

  /**
   * Load composer config for interaction application.
   */
  public async loadInteractionConfig({
    data: { metadata },
  }: TComposerSessionReadySignalEvent): Promise<IGuestInteractionApplicationConfig> {
    const connection: IVimeoApiConnection = metadata.connections.interactionTools;

    this.log.info('Loading interaction with new room API:', connection.uri);

    return postInteractionRoomAuth({
      connection,
      gctlConfig: queryManagerGctlConfig(this),
      fields: [
        'room_hash',
        'room_id',
        'firebase.app_name',
        'firebase.jwt',
        'firebase.config',
        'user',
        'metadata.connections.chat_history',
        'metadata.connections.questions_history',
        'metadata.connections.polls_history',
      ],
    });
  }

  /**
   * Load composer config for composer application.
   */
  public async loadComposerConfig({
    data: { metadata },
  }: TComposerSessionReadySignalEvent): Promise<ILiveComposerApplicationConfig> {
    return getBroadcasterComposerConfig({
      connection: metadata.connections.firebase,
      gctlConfig: queryManagerGctlConfig(this),
      fields: ['session_id', 'firebase.app_name', 'firebase.jwt', 'firebase.config', 'user'],
    });
  }

  /**
   * Load stream health config for the rtmp application.
   */
  public async loadStreamHealthCredentials(
    metadata: IBroadcasterComposerSessionInfoMetadata,
  ): Promise<IStreamHealthCredentials> {
    return getStreamHealthCredentials({
      connection: metadata.connections.streamHealth,
      gctlConfig: queryManagerGctlConfig(this),
      fields: [],
    });
  }

  /**
   * Firebase codebase loaded, so we are ready to work with JS code.
   */
  @OnSignal(EDependencySignal.FIREBASE_DEPENDENCIES_LOADED)
  protected async onFirebaseLoaded(signal: TFirebaseDependenciesLoadedSignalEvent): Promise<void> {
    this.firebase = signal.data;

    this.setContext({ isFirebaseReady: true });

    await Promise.all([tryToSetupInteractionConnection(this), tryToSetupComposerConnection(this)]);
  }

  @OnQuery(ELiveInteractionQuery.LIVE_INTERACTION_USER)
  protected onLiveInteractionUserRequested(): Optional<IInteractionApplicationUser> {
    return this.context.interactionCredentials?.user || null;
  }

  @OnQuery(ELiveConnectionQuery.FIREBASE_COMPOSER_APPLICATION)
  protected onComposerApplicationRequested(): Optional<TFirebaseApp> {
    return this.composerApp;
  }

  @OnQuery(ELiveConnectionQuery.FIREBASE_DEPENDENCIES)
  protected onFirebaseDependenciesRequested(): Optional<IFirebaseDependencies> {
    return this.firebase;
  }

  @OnQuery(ELiveConnectionQuery.FIREBASE_INTERACTION_CREDENTIALS)
  protected onFirebaseInteractionCredentialsRequested(): Maybe<IFirebaseInteractionCredentials> {
    return this.context.interactionCredentials;
  }

  @OnQuery(ELiveConnectionQuery.COMPOSER_FIREBASE_CONNECTION_STATE_READY)
  protected onComposerFirebaseConnectionStateReady(): boolean {
    return this.context.composerConnection?.isConnected;
  }

  @OnSignal(ELiveSignal.INTERACTION_SESSION_LOGOUT)
  protected onInteractionSessionLogout(): void {
    return disposeInteractionToolsConnection(this);
  }
}
